import {
  Assignment,
  DurationOfTemporaryEmployment,
  EmploymentType,
  JobAdIntegrationType,
  SalaryType,
  SectionProps,
} from "../../types/common";
import FormSectionHeader from "../FormSectionHeader";
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FormGrid from "../FormGrid";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import languages from "../../../../languages";
import PreviewField from "../PreviewField";
import useConditionalOpen from "../useConditionalOpen";
import { StyledSelect } from "../../styles";
import { useEffect, useMemo } from "react";
import getLocale from "../../../../common/helpers/getLocale";
import { htmlToPlainText, removeEmojis } from "../../utils";

const TyomarkkinatoriSection = ({
  isEditing,
  onPublishSelection,
  integrationsToPublish,
  selectedJobAd,
}: SectionProps) => {
  const integrationType = JobAdIntegrationType.Tyomarkkinatori;
  const integrationIsSelectedToBePublished =
    integrationsToPublish?.includes(integrationType);

  const {
    control,
    formState: { errors },
    watch,
    trigger,
  } = useFormContext();

  const [open, handleToggle] = useConditionalOpen(
    true,
    integrationIsSelectedToBePublished,
  );

  const { t } = useTranslation();
  const languageCode = getLocale().code;

  const sortedLanguages = useMemo(() => {
    const languagesArray = languages[languageCode];
    return languagesArray
      .filter((l) => l.code.length === 2) // Filter for two-letter codes
      .sort((a, b) => a.label.localeCompare(b.label)) // Sort alphabetically by label
      .map((l) => ({
        id: l.code,
        name: l.label,
      }));
  }, [languageCode]);

  const noteEURESSelected = watch("noteEURES");
  const otherThanIndefiniteEmploymentTypeSelected =
    watch("employmentType") !== EmploymentType.INDEFINITE;

  useEffect(() => {
    trigger();
  }, [integrationIsSelectedToBePublished, open, trigger, noteEURESSelected]);

  return (
    <>
      {(isEditing || integrationIsSelectedToBePublished) && (
        <>
          <FormSectionHeader
            isOpen={open}
            toggleOpen={handleToggle}
            title={"TYÖMARKKINATORI"}
            onPublishSelection={onPublishSelection}
            selectedJobAd={selectedJobAd}
            integrationType={integrationType}
            isEditing={isEditing}
            integrationIsSelectedToBePublished={
              integrationIsSelectedToBePublished
            }
          />
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid
              container
              alignItems="center"
              columnSpacing={2}
              rowGap={1}
              maxWidth={"1000px"}
            >
              <FormGrid
                title={t("jobAds:jobAdTextTyomarkkinatori")}
                contentSize={8}
                required
                infoText={t("jobAds:jobAdTextTyomarkkinatoriTooltip")}
              >
                <Controller
                  name="jobAdTextTyomarkkinatoriFinnish"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field }) => (
                    <>
                      {isEditing ? (
                        <Box>
                          {integrationIsSelectedToBePublished && (
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              sx={{
                                marginBottom: "5px",
                                borderRadius: "18px",
                              }}
                              onClick={() =>
                                field.onChange(
                                  removeEmojis(
                                    htmlToPlainText(
                                      watch("jobAdTextFinnish") || "",
                                    ),
                                  ),
                                )
                              }
                            >
                              {t("jobAds:copyJobAdText")}
                            </Button>
                          )}
                          <TextField
                            {...field}
                            size="small"
                            fullWidth
                            multiline
                            minRows={4}
                            error={
                              integrationIsSelectedToBePublished &&
                              !!errors.jobAdTextTyomarkkinatoriFinnish
                            }
                          />
                        </Box>
                      ) : (
                        <PreviewField text={field.value} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              {otherThanIndefiniteEmploymentTypeSelected && (
                <FormGrid
                  title={t("jobAds:durationOfTemporaryEmployment")}
                  contentSize={4}
                  required
                >
                  <Controller
                    name="durationOfTemporaryEmployment"
                    control={control}
                    rules={{
                      required:
                        integrationIsSelectedToBePublished &&
                        otherThanIndefiniteEmploymentTypeSelected,
                    }}
                    render={({ field: { value, onChange } }) => (
                      <>
                        {isEditing ? (
                          <StyledSelect
                            value={value}
                            onChange={(event) => onChange(event.target.value)}
                            size="small"
                            error={
                              integrationIsSelectedToBePublished &&
                              !!errors.durationOfTemporaryEmployment
                            }
                            MenuProps={{
                              disableScrollLock: true,
                            }}
                          >
                            {Object.values(DurationOfTemporaryEmployment)
                              .filter((value) => typeof value === "string")
                              .map((type) => (
                                <MenuItem key={type} value={type}>
                                  {t(
                                    `jobAds:durationOfTemporaryEmploymentOptions:${type}`,
                                  )}
                                </MenuItem>
                              ))}
                          </StyledSelect>
                        ) : (
                          <PreviewField
                            text={t(
                              `jobAds:durationOfTemporaryEmploymentOptions:${value}`,
                            )}
                          />
                        )}
                      </>
                    )}
                  />
                </FormGrid>
              )}

              <FormGrid
                title={t("jobAds:numberOfJobPositions")}
                contentSize={4}
                required
              >
                <Controller
                  name="numberOfJobPositions"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field }) => (
                    <>
                      {isEditing ? (
                        <TextField
                          {...field}
                          size="small"
                          fullWidth
                          type="number"
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.numberOfJobPositions
                          }
                        />
                      ) : (
                        <PreviewField text={field.value} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid title={t("jobAds:salaryType")} contentSize={4} required>
                <Controller
                  name="salaryType"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value}
                          onChange={(event) => onChange(event.target.value)}
                          size="small"
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.salaryType
                          }
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          {Object.values(SalaryType)
                            .filter((value) => typeof value === "string")
                            .map((type) => (
                              <MenuItem key={type} value={type}>
                                {t(`jobAds:${type}`)}
                              </MenuItem>
                            ))}
                        </StyledSelect>
                      ) : (
                        <PreviewField text={t(`jobAds:${value}`)} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid
                title={t("jobAds:workingLanguages")}
                contentSize={4}
                required
              >
                <Controller
                  control={control}
                  name="workingLanguages"
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <Autocomplete
                          value={value || []}
                          multiple
                          options={sortedLanguages}
                          renderOption={(props, option) => (
                            <ListItem {...props} key={option.id}>
                              <ListItemText>{option.name}</ListItemText>
                            </ListItem>
                          )}
                          getOptionLabel={(option) =>
                            sortedLanguages.find((l) => l.id === option.id)
                              ?.name || ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              name="workingLanguages"
                              error={
                                integrationIsSelectedToBePublished &&
                                !!errors.workingLanguages
                              }
                            />
                          )}
                          onChange={(_, newValue) => {
                            onChange(newValue);
                          }}
                        />
                      ) : (
                        <PreviewField
                          text={Object.values(value)
                            .map((v: any) => v.name)
                            .join(", ")}
                        />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid title={t("jobAds:assignment")} contentSize={4} required>
                <Controller
                  name="assignment"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value}
                          onChange={(event) => onChange(event.target.value)}
                          size="small"
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.assignment
                          }
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          {Object.values(Assignment)
                            .filter((value) => typeof value === "string")
                            .map((type) => (
                              <MenuItem key={type} value={type}>
                                {t(`jobAds:${type}`)}
                              </MenuItem>
                            ))}
                        </StyledSelect>
                      ) : (
                        <PreviewField text={t(`jobAds:${value}`)} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid
                title={t("jobAds:commissionCompanyName")}
                contentSize={4}
                required
              >
                <Controller
                  name="commissionCompanyName"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field }) => (
                    <>
                      {isEditing ? (
                        <TextField
                          {...field}
                          size="small"
                          fullWidth
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.commissionCompanyName
                          }
                        />
                      ) : (
                        <PreviewField text={field.value} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid
                title={t("jobAds:discloseCommissionCompany")}
                contentSize={2}
                required
              >
                <Controller
                  name="discloseCommissionCompany"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value == null ? "true" : value.toString()}
                          onChange={(event) =>
                            onChange(event.target.value === "true")
                          }
                          displayEmpty
                          size="small"
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          <MenuItem value="true">{t("yes")}</MenuItem>
                          <MenuItem value="false">{t("no")}</MenuItem>
                        </StyledSelect>
                      ) : (
                        <PreviewField text={value ? t("yes") : t("no")} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid
                title={t("jobAds:noteEURES")}
                contentSize={2}
                required
                infoText={t("jobAds:noteEURESTooltip")}
              >
                <Controller
                  name="noteEURES"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value == null ? "true" : value.toString()}
                          onChange={(event) =>
                            onChange(event.target.value === "true")
                          }
                          displayEmpty
                          size="small"
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          <MenuItem value="true">{t("yes")}</MenuItem>
                          <MenuItem value="false">{t("no")}</MenuItem>
                        </StyledSelect>
                      ) : (
                        <PreviewField text={value ? t("yes") : t("no")} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              {noteEURESSelected && (
                <>
                  <FormGrid
                    title={t("jobAds:EURESinstructionsFinnish")}
                    contentSize={4}
                    required
                  >
                    <Controller
                      name="EURESinstructionsFinnish"
                      control={control}
                      rules={{
                        required:
                          noteEURESSelected &&
                          integrationIsSelectedToBePublished,
                        validate: (value) => {
                          if (value.length > 5000) {
                            return t("jobAds:validation:EURESTooLong");
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <>
                          {isEditing ? (
                            <TextField
                              {...field}
                              size="small"
                              fullWidth
                              error={
                                integrationIsSelectedToBePublished &&
                                !!errors.EURESinstructionsFinnish
                              }
                              helperText={
                                errors.EURESinstructionsFinnish?.message &&
                                typeof errors.EURESinstructionsFinnish
                                  .message === "string"
                                  ? errors.EURESinstructionsFinnish.message
                                  : ""
                              }
                              FormHelperTextProps={{
                                style: {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          ) : (
                            <PreviewField text={field.value} />
                          )}
                        </>
                      )}
                    />
                  </FormGrid>

                  {/** EURES english instructions are disabled at the moment (YLPE-2405 should tackle this issue)
                  <FormGrid
                    title={t("jobAds:EURESinstructionsEnglish")}
                    contentSize={4}
                    required
                  >
                    <Controller
                      name="EURESinstructionsEnglish"
                      control={control}
                      rules={{
                        required:
                          noteEURESSelected &&
                          integrationIsSelectedToBePublished,
                      }}
                      render={({ field }) => (
                        <>
                          {isEditing ? (
                            <TextField
                              {...field}
                              size="small"
                              fullWidth
                              error={
                                integrationIsSelectedToBePublished &&
                                !!errors.EURESinstructionsEnglish
                              }
                            />
                          ) : (
                            <PreviewField text={field.value} />
                          )}
                        </>
                      )}
                    />
                  </FormGrid>
                  **/}
                </>
              )}
            </Grid>
          </Collapse>
          <Divider
            sx={{
              margin: "24px 0 16px 0",
            }}
          />
        </>
      )}
    </>
  );
};

export default TyomarkkinatoriSection;
